import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // One-time migration: an earlier version of this file auto-saved the
    // OS/browser's dark-mode preference into localStorage as if it were an
    // explicit user choice. Reset once so everyone lands back on the app's
    // real default (light) — after this, an explicit Preferences toggle
    // persists normally, same as always.
    if (!localStorage.getItem('theme-migrated-v2')) {
      localStorage.removeItem('theme');
      localStorage.setItem('theme-migrated-v2', '1');
      return 'light';
    }

    // The app's default theme is light, regardless of OS/browser preference —
    // dark mode is opt-in only, via the explicit toggle in Preferences.
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' ? 'dark' : 'light';
  });

  // Apply theme changes
  useEffect(() => {
    // Update data-bs-theme attribute on document and html element
    document.documentElement.setAttribute('data-bs-theme', theme);
    document.documentElement.style.colorScheme = theme;
    
    // Update body class and background
    document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
    document.body.style.backgroundColor = theme === 'dark' ? '#212529' : '#ffffff';
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const setThemeMode = (newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setTheme(newTheme);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
}; 